import React, { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig, RoleUtils } from '@galaxy/lorule-react-commons';
import { Modal, Button } from 'react-bootstrap';
import { ResponseError, SubmitButton } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from '../../common/modal/NinModal';
import { sendToast, formatAmDisplayString } from '../../../utils/displayUtils';
import { addComment } from '../../../api/actionMemoApi';
import TextCountedField from '../../common/TextCountedField';
import { COMMENT_MAX_LENGTH } from '../../../utils/Constants';

export interface AmCommentFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface AmCommentModalProps extends AmCommentFormBaseProps {
    amId: number;
    amNumber: number;
    supNumber: number;
}

const submitAmComment = async (
    config: LoruleRequestConfig,
    values: {
        amId: number;
        amNumber: number;
        supNumber: number;
        commentMessage: string;
    },
    roles: string[],
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        await addComment(
            config,
            values.amId,
            values.commentMessage,
            !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
        );
        sendToast(
            <>
                Added Comment:{' '}
                <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
            </>,
        );

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Comment failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AmCommentModal = (props: AmCommentModalProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const [responseError, setResponseError] = useState('');

    const addCommentValidationSchema = Yup.object().shape({
        commentMessage: Yup.string()
            .required('Comment is required')
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters'),
    });

    const initialValues = {
        amId: props.amId,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        commentMessage: '',
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={addCommentValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitAmComment(
                            config,
                            values,
                            roles,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({ values, touched, errors, isSubmitting, dirty }) => (
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>Add Comment</Modal.Title>
                            </Modal.Header>
                            <Form>
                                <Modal.Body>
                                    <ResponseError errorMessage={responseError} />

                                    <TextCountedField
                                        label="Comment"
                                        name="commentMessage"
                                        touched={touched.commentMessage}
                                        errors={errors.commentMessage}
                                        isRequired
                                        testId="commentMessage"
                                        length={
                                            values.commentMessage ? values.commentMessage.length : 0
                                        }
                                        maxLength={COMMENT_MAX_LENGTH}
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={toggleModal}
                                        disabled={isSubmitting}
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton
                                        className="btn-primary ml-auto"
                                        disabled={!dirty}
                                        isSubmitting={isSubmitting}
                                        buttonText="Add"
                                    />
                                </Modal.Footer>
                            </Form>
                        </>
                    )}
                />
            )}
        />
    );
};

export default AmCommentModal;
